/**
 * Clip a user-drawn polygon to the property border (GeoJSON [lng, lat], same as polygon-clipping).
 * Uses {@link https://github.com/mfogel/polygon-clipping polygon-clipping} (Martinez–Rueda).
 */

import polygonClipping from 'polygon-clipping';

/**
 * Returns the part of `feature` inside the property; `null` if nothing remains.
 * If there is no property geometry, returns `feature` unchanged.
 *
 * @param {{ geometry?: object, properties?: object }} feature
 * @param {unknown} borderCoords From property bundle — `geometry.coordinates` for Polygon, or use `propertyGeometry`
 * @param {object | null | undefined} propertyGeometry Preferred clip source (Polygon or MultiPolygon)
 * @returns {object | null}
 */
export function clipDrawnPolygonToPropertyBorder(feature, borderCoords, propertyGeometry) {
  const toClip = (geom) => {
    if (!geom?.coordinates?.length) return null;
    if (geom.type === 'Polygon') return [geom.coordinates];
    if (geom.type === 'MultiPolygon') return geom.coordinates;
    return null;
  };

  const clip = toClip(propertyGeometry) ?? (borderCoords?.length ? [borderCoords] : null);
  const subject = toClip(feature?.geometry);
  if (!clip || !subject) {
    return feature;
  }
  try {
    const result = polygonClipping.intersection(subject, clip);
    if (!result?.length) return null;
    const props =
      feature.properties && typeof feature.properties === 'object' ? feature.properties : {};
    const geometry =
      result.length === 1
        ? { type: 'Polygon', coordinates: result[0] }
        : { type: 'MultiPolygon', coordinates: result };
    return { type: 'Feature', properties: { ...props }, geometry };
  } catch (e) {
    console.error('clipDrawnPolygonToPropertyBorder', e);
    return feature;
  }
}
